import {
  apiSlice,
  createClientV2MutationFn,
  createClientV2QueryFn,
} from 'store/slices/api';
import { ListResponse, QueryParams } from 'types/api';
import { parseQueryParams } from 'store/slices/utils';
import {
  BomUnifiedPATCHSchema,
  BomUnifiedPOSTSchema,
  BomUnifiedSchema,
} from 'types/unified/boms';
import {
  UNIFIED_MODEL_NAMES,
  UNIFIED_URLS,
} from 'store/slices/constants/unified';
import {
  UnifiedDELETERequest,
  UnifiedDELETEResponse,
  UnifiedPATCHRequest,
  UnifiedPATCHResponse,
  UnifiedPOSTRequest,
  UnifiedPOSTResponse,
  UnifiedToggleRequest,
  UnifiedToggleResponse,
} from 'types/unified/api';

export const extendedApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getUnifiedBoms: builder.query<
      ListResponse<BomUnifiedSchema>,
      QueryParams<BomUnifiedSchema>
    >({
      queryFn: createClientV2QueryFn<
        ListResponse<BomUnifiedSchema>,
        QueryParams<BomUnifiedSchema>
      >((params) => ({
        url: UNIFIED_URLS.BOMS(parseQueryParams<BomUnifiedSchema>(params)),
      })),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(
                ({ id }: BomUnifiedSchema) =>
                  ({
                    type: 'Boms',
                    id,
                  } as const)
              ),
              { type: 'Boms', id: 'LIST' },
            ]
          : [{ type: 'Boms', id: 'LIST' }],
    }),
    createUnifiedBoms: builder.mutation<
      UnifiedPOSTResponse<BomUnifiedSchema>,
      UnifiedPOSTRequest<BomUnifiedPOSTSchema>
    >({
      queryFn: createClientV2MutationFn<
        UnifiedPOSTResponse<BomUnifiedSchema>,
        UnifiedPOSTRequest<BomUnifiedPOSTSchema>
      >((payload) => ({
        url: UNIFIED_URLS.BOMS(),
        method: 'POST',
        body: payload,
      })),
      invalidatesTags: (result) => [
        ...(result?.createdIds?.map((id) => ({ type: 'Boms' as const, id })) ||
          []),
        { type: 'Boms' as const, id: 'LIST' },
      ],
    }),
    updateUnifiedBoms: builder.mutation<
      UnifiedPATCHResponse<BomUnifiedSchema>,
      UnifiedPATCHRequest<BomUnifiedPATCHSchema>
    >({
      queryFn: createClientV2MutationFn<
        UnifiedPATCHResponse<BomUnifiedSchema>,
        UnifiedPATCHRequest<BomUnifiedPATCHSchema>
      >((payload) => ({
        url: UNIFIED_URLS.BOMS(),
        method: 'PATCH',
        body: payload,
      })),
      invalidatesTags: (result) => [
        ...(result?.updatedIds?.map((id) => ({ type: 'Boms' as const, id })) ||
          []),
        { type: 'Boms' as const, id: 'LIST' },
      ],
    }),
    deleteUnifiedBoms: builder.mutation<
      UnifiedDELETEResponse<BomUnifiedSchema>,
      UnifiedDELETERequest<BomUnifiedSchema>
    >({
      queryFn: createClientV2MutationFn<
        UnifiedDELETEResponse<BomUnifiedSchema>,
        UnifiedDELETERequest<BomUnifiedSchema>
      >((payload) => ({
        url: UNIFIED_URLS.BOMS(),
        method: 'DELETE',
        body: payload,
      })),
      invalidatesTags: (result) => [
        ...(result?.deletedIds?.map((id) => ({ type: 'Boms' as const, id })) ||
          []),
        { type: 'Boms', id: 'LIST' },
      ],
    }),
    approveUnifiedBoms: builder.mutation<
      UnifiedToggleResponse<BomUnifiedSchema>,
      UnifiedToggleRequest<BomUnifiedSchema>
    >({
      queryFn: createClientV2MutationFn<
        UnifiedToggleResponse<BomUnifiedSchema>,
        UnifiedToggleRequest<BomUnifiedSchema>
      >((payload) => ({
        url: UNIFIED_URLS.TOGGLE_APPROVE(UNIFIED_MODEL_NAMES.BOMS),
        method: 'POST',
        body: payload,
      })),
      invalidatesTags: (result) => [
        ...(result?.updatedIds?.map((id) => ({ type: 'Boms' as const, id })) ||
          []),
        { type: 'Boms', id: 'LIST' },
      ],
    }),
    unapproveUnifiedBoms: builder.mutation<
      UnifiedToggleResponse<BomUnifiedSchema>,
      UnifiedToggleRequest<BomUnifiedSchema>
    >({
      queryFn: createClientV2MutationFn<
        UnifiedToggleResponse<BomUnifiedSchema>,
        UnifiedToggleRequest<BomUnifiedSchema>
      >((payload) => ({
        url: UNIFIED_URLS.TOGGLE_UNAPPROVE(UNIFIED_MODEL_NAMES.BOMS),
        method: 'POST',
        body: payload,
      })),
      invalidatesTags: (result) => [
        ...(result?.updatedIds?.map((id) => ({ type: 'Boms' as const, id })) ||
          []),
        { type: 'Boms', id: 'LIST' },
      ],
    }),
    archiveUnifiedBoms: builder.mutation<
      UnifiedToggleResponse<BomUnifiedSchema>,
      UnifiedToggleRequest<BomUnifiedSchema>
    >({
      queryFn: createClientV2MutationFn<
        UnifiedToggleResponse<BomUnifiedSchema>,
        UnifiedToggleRequest<BomUnifiedSchema>
      >((payload) => ({
        url: UNIFIED_URLS.TOGGLE_ARCHIVE(UNIFIED_MODEL_NAMES.BOMS),
        method: 'POST',
        body: payload,
      })),
      invalidatesTags: (result) => [
        ...(result?.updatedIds?.map((id) => ({ type: 'Boms' as const, id })) ||
          []),
        { type: 'Boms', id: 'LIST' },
      ],
    }),
    unarchiveUnifiedBoms: builder.mutation<
      UnifiedToggleResponse<BomUnifiedSchema>,
      UnifiedToggleRequest<BomUnifiedSchema>
    >({
      queryFn: createClientV2MutationFn<
        UnifiedToggleResponse<BomUnifiedSchema>,
        UnifiedToggleRequest<BomUnifiedSchema>
      >((payload) => ({
        url: UNIFIED_URLS.TOGGLE_UNARCHIVE(UNIFIED_MODEL_NAMES.BOMS),
        method: 'POST',
        body: payload,
      })),
      invalidatesTags: (result) => [
        ...(result?.updatedIds?.map((id) => ({ type: 'Boms' as const, id })) ||
          []),
        { type: 'Boms', id: 'LIST' },
      ],
    }),
    lockUnifiedBoms: builder.mutation<
      UnifiedToggleResponse<BomUnifiedSchema>,
      UnifiedToggleRequest<BomUnifiedSchema>
    >({
      queryFn: createClientV2MutationFn<
        UnifiedToggleResponse<BomUnifiedSchema>,
        UnifiedToggleRequest<BomUnifiedSchema>
      >((payload) => ({
        url: UNIFIED_URLS.TOGGLE_LOCK(UNIFIED_MODEL_NAMES.BOMS),
        method: 'POST',
        body: payload,
      })),
      invalidatesTags: (result) => [
        ...(result?.updatedIds?.map((id) => ({ type: 'Boms' as const, id })) ||
          []),
        { type: 'Boms', id: 'LIST' },
      ],
    }),
    unlockUnifiedBoms: builder.mutation<
      UnifiedToggleResponse<BomUnifiedSchema>,
      UnifiedToggleRequest<BomUnifiedSchema>
    >({
      queryFn: createClientV2MutationFn<
        UnifiedToggleResponse<BomUnifiedSchema>,
        UnifiedToggleRequest<BomUnifiedSchema>
      >((payload) => ({
        url: UNIFIED_URLS.TOGGLE_UNLOCK(UNIFIED_MODEL_NAMES.BOMS),
        method: 'POST',
        body: payload,
      })),
      invalidatesTags: (result) => [
        ...(result?.updatedIds?.map((id) => ({ type: 'Boms' as const, id })) ||
          []),
        { type: 'Boms', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  usePrefetch,
  useGetUnifiedBomsQuery,
  useCreateUnifiedBomsMutation,
  useUpdateUnifiedBomsMutation,
  useDeleteUnifiedBomsMutation,
  useApproveUnifiedBomsMutation,
  useUnapproveUnifiedBomsMutation,
  useArchiveUnifiedBomsMutation,
  useUnarchiveUnifiedBomsMutation,
  useLockUnifiedBomsMutation,
  useUnlockUnifiedBomsMutation,
} = extendedApiSlice;
